import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  X,
  Upload,
  Loader2,
  Image as ImageIcon,
} from "lucide-react";
import { Button } from "../../Components/Button";
import { Badge } from "../../Components/Badge";
import { Input } from "../../Components/Input";
import { toast } from "sonner";
import { Toaster } from "sonner";

import { decryptData } from '../../../utils/storageCrypto';

export function CreateRequestModal({ type, onClose }) {
  /* -------------------------------------------------------------------------- */
  /*                                   STATES                                   */
  /* -------------------------------------------------------------------------- */
  const backendBaseUrl = process.env.REACT_APP_FORE_BRICKBUCKET;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [customer, setCustomer] = useState(null);

  const [orderItems, setOrderItems] = useState([]);

  const [reasonOptions, setReasonOptions] = useState([]);

  const [imageFiles, setImageFiles] = useState([]);

  const [form, setForm] = useState({
    order_id: "",
    customer_id: "",
    items: [],
    reason_code: "",
    return_mode: "PAID_PICKUP",
    media: [],
  });

  /* -------------------------------------------------------------------------- */
  /*                                API PLACEHOLDERS                            */
  /* -------------------------------------------------------------------------- */

  /**
   * Search Orders
   *
   * Replace URL later.
   */
  const searchOrders = async (keyword) => {
    if (!keyword.trim()) {
      setOrders([]);
      return;
    }

    try {
      const res = await fetch(
        `${backendBaseUrl}customer/salesorder/get-all-salesorders?status=Out for Delivery,Delivered`
      );

      const json = await res.json();

      const allOrders = json.data || [];

      const filtered = allOrders.filter(order =>
        (order.quick_buy_order_number || order.salesorder_number || "")
          .toLowerCase()
          .includes(keyword.toLowerCase())
      );

      setOrders(filtered);

      //setOrders(json.data || []);
    } catch (err) {
      console.error(err);
      setOrders([]);
    }
  };

  /**
   * Get complete order details
   */
  const fetchOrderDetails = async (orderId) => {
    try {
      setLoading(true);

      const res = await fetch(`${backendBaseUrl}customer/salesorder/get-salesorder?salesorder_id=${orderId}`);

      const json = await res.json();
      console.log("json:",json);

      /**
       * Expected response
       *
       * {
       *   customer:{}
       *   items:[]
       * }
       */

      setCustomer(json.data.customer_id);

      setOrderItems(json.data.items || []);

      setForm((prev) => ({
        ...prev,
        order_id: orderId,
        customer_id: json.data.customer_id?._id || "",
        items: [],
      }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reasons API
   */

  const fetchReasons = async () => {
    try {
      const type = "return";  
      const res = await fetch(`${backendBaseUrl}returns/get-reasons?type=${type}`);

      const json = await res.json();

      setReasonOptions(json.data || []);
    } catch (err) {
      console.error(err);

      /**
       * fallback
       */

      setReasonOptions([
        {
          code: "DAMAGED_PRODUCT",
          name: "Damaged Product",
        },
        {
          code: "WRONG_PRODUCT",
          name: "Wrong Product",
        },
        {
          code: "DEFECTIVE_PRODUCT",
          name: "Defective Product",
        },
        {
          code: "QUALITY_ISSUE",
          name: "Quality Issue",
        },
      ]);
    }
  };

  /**
   * Upload Images
   *
   * Replace URL later
   */

  const uploadImages = async () => {
    if (!imageFiles.length) return [];

    const uploaded = [];

    for (const file of imageFiles) {
      const fd = new FormData();

      fd.append("file", file);

      const res = await fetch(`${backendBaseUrl}/upload`, {
        method: "POST",
        body: fd,
      });

      const json = await res.json();

      uploaded.push({
        media_type: file.type,
        url: json.url,
      });
    }

    return uploaded;
  };

  /* -------------------------------------------------------------------------- */
  /*                                  HELPERS                                   */
  /* -------------------------------------------------------------------------- */

  const debounceSearch = useMemo(() => {
    let timeout;

    return (value) => {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        searchOrders(value);
      }, 500);
    };
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /* -------------------------------------------------------------------------- */
  /*                              ITEM SELECTION                                */
  /* -------------------------------------------------------------------------- */

  const toggleItem = (item) => {
    setForm((prev) => {
      const exists = prev.items.find(
        (x) => x.item_id === item.item_id
      );

      if (exists) {
        return {
          ...prev,
          items: prev.items.filter(
            (x) => x.item_id !== item.item_id
          ),
        };
      }

      return {
        ...prev,
        items: [
          ...prev.items,
          {
            item_id: item.item_id,
            item_name: item.item_name,
            requested_qty: item.receivable_quantity || item.delivered_quantity,
          },
        ],
      };
    });
  };

  const updateQty = (itemId, qty) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.item_id === itemId
          ? {
              ...item,
              requested_qty: Number(qty),
            }
          : item
      ),
    }));
  };

  const isSelected = (id) =>
    form.items.some((x) => x.item_id === id);

  const getSelectedQty = (id) => {
    return (
      form.items.find((x) => x.item_id === id)?.requested_qty ?? 1
    );
  };

  /* -------------------------------------------------------------------------- */
  /*                               IMAGE PICKER                                 */
  /* -------------------------------------------------------------------------- */

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    setImageFiles((prev) => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setImageFiles((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  /* -------------------------------------------------------------------------- */
  /*                                 EFFECTS                                    */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    fetchReasons();
  }, []);


  useEffect(() => {
    if (searchText.trim().length < 2) {
        setOrders([]);
        return;
    }

    debounceSearch(searchText);
}, [searchText]);

  useEffect(() => {
    //debounceSearch(searchText);
  }, [searchText]);

const handleSubmit = async () => {
  console.log("submitting return request");
  try {
      console.log("submitting return request in try block");

    /* ------------------------------------------------------- */
    /* VALIDATION */
    /* ------------------------------------------------------- */

    if (!form.order_id) {
      toast.error("Please select an order.");
      return;
    }

    if (!form.items.length) {
      toast.error("Please select at least one item.");
      return;
    }

    if (!form.reason_code) {
      toast.error("Please select a reason.");
      return;
    }

    if (
      type === "return" &&
      !form.return_mode
    ) {
      toast.error("Please select return mode.");
      return;
    }

    for (const item of form.items) {
      if (
        !item.requested_qty ||
        Number(item.requested_qty) <= 0
      ) {
        toast.error("Invalid requested quantity.");
        return;
      }
    }

    setSaving(true);

    const formData = new FormData();

    formData.append("order_id", form.order_id);
    formData.append("customer_id", form.customer_id);
    formData.append("reason_code", form.reason_code);
    formData.append("return_mode", form.return_mode);

    formData.append("items", JSON.stringify(form.items));

    imageFiles.forEach(file => {
        formData.append("media", file);
    });

    /* ------------------------------------------------------- */
    /* API */
    /* ------------------------------------------------------- */
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    const encryptedhubId = sessionStorage.getItem('hubId');
    const hubId = decryptData(encryptedhubId);

    console.log(formData)
    const endpoint =
      type === "return"
        ? `${backendBaseUrl}returns/request-return`
        : `${backendBaseUrl}returns/request-replacement`;

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "x-token": token,
      },
      body: formData,
    });

    const json = await response.json();
    console.log("response:", json);

    if (json.success === false) {
      const message = json.message || json.error
      //toast.error(message);
      alert(message);

      return;
    }

    /* toast.success( */
      //json.message || "Return request submitted successfully."
      alert(json.message);
   // );

    // Close after user sees the success toast
    setTimeout(() => {
      onClose();
    }, 1000);


  }catch (err) {
  console.error(err);

  toast.error(
    err?.message || "Unable to submit the request."
  );
} finally {
    setSaving(false);
  }
};

    return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden">

        {/* Header */}

        <div className="flex items-center justify-between border-b px-6 py-4">

          <div>
            <h2 className="text-xl font-semibold">
              Raise {type === "return" ? "Return" : "Replacement"} Request
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Select an order and choose the items.
            </p>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>

        </div>

        <div className="overflow-y-auto max-h-[78vh]">

          <div className="p-6 space-y-6">

            {/* -------------------- ORDER SEARCH -------------------- */}

            <div>

              <label className="font-medium mb-2 block">
                Order *
              </label>

              <div className="relative">

                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400"/>

                <Input
                  className="pl-10"
                  placeholder="Search Order Number..."
                  value={searchText}
                  onChange={(e)=>setSearchText(e.target.value)}
                />

                {orders.length > 0 && (

                  <div className="absolute left-0 right-0 top-12 bg-white border rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">

                    {orders.map(order=>(
                      <button
                        key={order._id}
                        type="button"
                        className={`w-full text-left px-4 py-3 border-b hover:bg-gray-100 ${
                            selectedOrder?._id === order._id
                                ? "bg-blue-50"
                                : ""
                        }`}
                        onClick={async ()=>{
                          setSelectedOrder(order);
                          setSearchText(order.quick_buy_order_number || order.salesorder_number);
                          setOrders([]);
                          setShowDropdown(false);
                          await fetchOrderDetails(order._id);
                        }}
                      >

                        <div className="font-medium">
                          {order.quick_buy_order_number || order.salesorder_number}
                        </div>

                        <div className="text-xs text-gray-500">
                          {order.customer_name}
                        </div>

                      </button>
                    ))}

                  </div>

                )}

              </div>

            </div>

            {/* -------------------- CUSTOMER CARD -------------------- */}

            {customer && (

              <div className="rounded-lg border bg-gray-50 p-4">

                <div className="grid grid-cols-3 gap-6">

                  <div>

                    <div className="text-xs text-gray-500">
                      Customer
                    </div>

                    <div className="font-medium">
                      {customer.customer_name}
                    </div>

                  </div>

                  <div>

                    <div className="text-xs text-gray-500">
                      Mobile
                    </div>

                    <div>
                      {customer.mobile_number}
                    </div>

                  </div>

                  <div>

                    <div className="text-xs text-gray-500">
                      Email
                    </div>

                    <div>
                      {customer.email || "-"}
                    </div>

                  </div>

                </div>

              </div>

            )}

            {/* -------------------- ITEMS -------------------- */}

            {loading ? (

              <div className="flex justify-center py-10">

                <Loader2 className="h-7 w-7 animate-spin"/>

              </div>

            ) : orderItems.length > 0 && (

              <div>

                <div className="font-medium mb-3">
                  Select Items
                </div>

                <div className="border rounded-lg overflow-hidden">

                  <table className="w-full">

                    <thead className="bg-gray-100">

                      <tr>

                        <th className="text-left px-4 py-3 w-14">
                        </th>

                        <th className="text-left">
                          Product
                        </th>

                        <th>
                          Ordered
                        </th>

                        <th>
                          Delivered
                        </th>

                        <th>
                          Eligible
                        </th>

                        <th className="w-36">
                          Request Qty
                        </th>

                      </tr>

                    </thead>

                    <tbody>

                      {orderItems.map(item=>(

                        <tr
                          key={item.item_id}
                          className="border-t"
                        >

                          <td className="text-center">

                            <input
                              type="checkbox"
                              checked={isSelected(item.item_id)}
                              onChange={()=>toggleItem(item)}
                            />

                          </td>

                          <td className="px-4 py-3">

                            <div className="font-medium">
                              {item.item_name}
                            </div>

                            <div className="text-xs text-gray-500">
                              SKU : {item.sku}
                            </div>

                          </td>

                          <td className="text-center">
                            {item.quantity}
                          </td>

                          <td className="text-center">
                            {item.receivable_quantity || item.delivered_quantity}
                          </td>

                          <td className="text-center">
                            {item.quantity}
                          </td>

                          <td className="px-2">

                            {isSelected(item.item_id) && (

                              <Input
                                type="number"
                                min={1}
                                max={item.delivered_quantity || item.receivable_quantity}
                                value={getSelectedQty(item.item_id)}
                                onChange={(e)=>
                                  updateQty(
                                    item.item_id,
                                    e.target.value
                                  )
                                }
                              />

                            )}

                          </td>

                        </tr>

                      ))}

                    </tbody>

                  </table>

                </div>

              </div>

            )}
                        {/* -------------------- REASON -------------------- */}

            <div className="grid grid-cols-2 gap-6">

              <div>

                <label className="font-medium mb-2 block">
                  Reason <span className="text-red-500">*</span>
                </label>

                <select
                  className="w-full border rounded-md h-10 px-3"
                  value={form.reason_code}
                  onChange={(e) =>
                    handleChange("reason_code", e.target.value)
                  }
                >
                  <option value="">
                    Select Reason
                  </option>

                  {reasonOptions.map((reason) => (
                    <option
                      key={reason.code}
                      value={reason.code}
                    >
                      {reason.label || reason.name}
                    </option>
                  ))}

                </select>

              </div>

              {type === "return" && (

                <div>

                  <label className="font-medium mb-2 block">
                    Return Mode
                  </label>

                  <select
                    className="w-full border rounded-md h-10 px-3"
                    value={form.return_mode}
                    onChange={(e) =>
                      handleChange(
                        "return_mode",
                        e.target.value
                      )
                    }
                  >
                    <option value="PAID_PICKUP">
                      Paid Pickup
                    </option>

                    <option value="HUB_DROP">
                      Hub Drop
                    </option>

                  </select>

                </div>

              )}

            </div>

            {/* -------------------- IMAGE UPLOAD -------------------- */}

            <div>

              <label className="font-medium mb-2 block">
                Photos
              </label>

              <label
                htmlFor="return-images"
                className="
                  flex
                  flex-col
                  items-center
                  justify-center
                  border-2
                  border-dashed
                  rounded-lg
                  p-8
                  cursor-pointer
                  hover:bg-gray-50
                "
              >

                <Upload className="h-8 w-8 text-gray-400 mb-2"/>

                <div className="font-medium">
                  Click to upload
                </div>

                <div className="text-sm text-gray-500 mt-1">
                  JPG, PNG, JPEG
                </div>

              </label>

              <input
                id="return-images"
                hidden
                multiple
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />

            </div>

            {/* -------------------- IMAGE PREVIEW -------------------- */}

            {imageFiles.length > 0 && (

              <div>

                <div className="font-medium mb-3">
                  Uploaded Images
                </div>

                <div className="grid grid-cols-5 gap-4">

                  {imageFiles.map((file,index)=>(

                    <div
                      key={index}
                      className="relative border rounded-lg overflow-hidden"
                    >

                      <img
                        src={URL.createObjectURL(file)}
                        alt=""
                        className="h-28 w-full object-cover"
                      />

                      <button
                        type="button"
                        className="
                          absolute
                          top-2
                          right-2
                          bg-red-500
                          rounded-full
                          p-1
                          text-white
                        "
                        onClick={()=>removeImage(index)}
                      >

                        <X className="h-3 w-3"/>

                      </button>

                    </div>

                  ))}

                </div>

              </div>

            )}

            {/* -------------------- SUMMARY -------------------- */}

            <div className="rounded-lg bg-gray-50 border p-4">

              <div className="grid grid-cols-4 gap-6">

                <div>

                  <div className="text-xs text-gray-500">
                    Selected Items
                  </div>

                  <div className="font-semibold text-lg">
                    {form.items.length}
                  </div>

                </div>

                <div>

                  <div className="text-xs text-gray-500">
                    Total Qty
                  </div>

                  <div className="font-semibold text-lg">

                    {
                      form.items.reduce(
                        (sum,item)=>
                          sum+Number(item.requested_qty),
                        0
                      )
                    }

                  </div>

                </div>

                <div>

                  <div className="text-xs text-gray-500">
                    Type
                  </div>

                  <div className="font-semibold">

                    {type==="return"
                      ? "Return"
                      : "Replacement"}

                  </div>

                </div>

                <div>

                  <div className="text-xs text-gray-500">
                    Images
                  </div>

                  <div className="font-semibold">
                    {imageFiles.length}
                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

        {/* -------------------- FOOTER -------------------- */}

        <div className="border-t px-6 py-4 pb-8 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            variant="outline"
            disabled={saving}
            onClick={handleSubmit}
          >

            {saving && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin"/>
            )}

            Raise {type==="return"
              ? "Return"
              : "Replacement"}

          </Button>

        </div>

      </div>

    </div>
  );
}